import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '../../../api/client';
import { CollapsibleSection, inputCls } from './shared';

interface CustomerType {
  id: string;
  name: string;
  createdBy: string | null;
  createdAt: string;
}

interface CustomerTag {
  id: string;
  name: string;
  color: string | null;
  createdBy: string | null;
  createdAt: string;
}

const TAG_COLORS = [
  '#14b8a6','#10b981','#f59e0b','#ef4444','#f97316',
  '#06b6d4','#64748b','#ec4899','#84cc16','#a16207',
];

/* ---- 图标映射 ---- */
const TYPE_ICONS: Record<string, string> = {
  '个人': 'ri-user-line',
  '企业': 'ri-building-line',
  'VIP': 'ri-star-line',
  '会员': 'ri-vip-crown-line',
  '潜在': 'ri-heart-line',
  '老客户': 'ri-history-line',
  '新客户': 'ri-fire-line',
  'default': 'ri-user-settings-line',
};

function getTypeIcon(name: string): string {
  for (const [key, icon] of Object.entries(TYPE_ICONS)) {
    if (name.includes(key)) return icon;
  }
  return TYPE_ICONS.default;
}

/* ---- 骨架屏 ---- */
function SkeletonList({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 bg-white border border-gray-100 rounded-xl">
          <div className="w-9 h-9 rounded-lg bg-gray-100 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3.5 bg-gray-100 rounded w-24 animate-pulse" />
            <div className="h-2.5 bg-gray-50 rounded w-16 animate-pulse" />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---- 空态 ---- */
function EmptyState({ icon, text, hint }: { icon: string; text: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center">
      <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-2xl mb-3">
        <i className={`${icon} text-2xl text-gray-300`} />
      </div>
      <p className="text-sm text-gray-400 font-medium">{text}</p>
      {hint && <p className="text-xs text-gray-300 mt-1">{hint}</p>}
    </div>
  );
}

/* ---- 确认弹窗 ---- */
function ConfirmDialog({
  open,
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = '确认',
  cancelText = '取消',
  variant = 'danger',
}: {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-[fadeInUp_0.2s_ease-out]">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${
            variant === 'danger' ? 'bg-red-50 text-red-500' : 'bg-teal-50 text-teal-500'
          }`}>
            <i className={variant === 'danger' ? 'ri-alert-line' : 'ri-information-line'} />
          </div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        </div>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex gap-3">
          <button type="button" onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer">
            {cancelText}
          </button>
          <button type="button" onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors cursor-pointer ${
              variant === 'danger'
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-teal-500 hover:bg-teal-600 text-white'
            }`}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- Toast 提示 ---- */
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <div className="fixed top-4 right-4 z-50 animate-[slideInRight_0.3s_ease-out]">
      <div className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg ${
        type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'
      }`}>
        <i className={type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
        <span className="text-sm font-medium">{message}</span>
        <button type="button" onClick={onClose} className="ml-2 opacity-80 hover:opacity-100">
          <i className="ri-close-line" />
        </button>
      </div>
    </div>
  );
}

/* ========== 客户类型管理 ========== */
function CustomerTypesManager() {
  const queryClient = useQueryClient();
  const [addName, setAddName] = useState('');
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  /** 失败提示必须把服务端那句中文带回来：归属/预设锁定这类拒绝只有后端知道自己为什么挡 */
  const notify = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), type === 'error' ? 3500 : 2500);
  };
  const fail = (action: string, fallback: string) => (error: unknown) => {
    console.error(`${action}:`, error);
    notify(error instanceof Error && error.message ? error.message : fallback, 'error');
  };

  // 服务端只回「系统预设 + 本人自建」，所以「能改」等价于「createdBy 不为空」
  const { data: types = [], isLoading: loading } = useQuery({
    queryKey: ['customerTypes'],
    queryFn: async () => {
      const data = await get<CustomerType[]>('/customer-types');
      return data ?? [];
    },
  });

  // created_by 由服务端按登录态注入，客户端不再上送
  const addCustomerTypeMutation = useMutation({
    mutationFn: async (name: string) => {
      return post('/customer-types', { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerTypes'] });
      setAddName('');
      setAdding(false);
      notify('客户类型添加成功');
    },
    onError: fail('添加客户类型失败', '添加失败，请重试'),
  });

  const editCustomerTypeMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      return put(`/customer-types/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerTypes'] });
      setEditId(null);
      notify('客户类型已更新');
    },
    onError: fail('编辑客户类型失败', '更新失败，请重试'),
  });

  const deleteCustomerTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      return del(`/customer-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerTypes'] });
      setDeleteId(null);
      setShowConfirm(false);
      notify('客户类型已删除');
    },
    onError: fail('删除客户类型失败', '删除失败，请重试'),
  });

  const handleAdd = () => {
    if (!addName.trim()) return;
    addCustomerTypeMutation.mutate(addName.trim());
  };

  const handleEdit = (id: string) => {
    if (!editName.trim()) return;
    editCustomerTypeMutation.mutate({ id, name: editName.trim() });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteCustomerTypeMutation.mutate(deleteId);
    }
  };

  return (
    <div>
      {/* 头部卡片 */}
      <div className="flex items-center justify-between mb-4 p-4 bg-white border border-gray-100 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl">
            <i className="ri-user-settings-line text-teal-500 text-lg" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">客户类型</p>
            <p className="text-xs text-gray-400 mt-0.5">
              系统预设人人可选，自己新增的类型仅本人可见可改
            </p>
          </div>
        </div>
        <button type="button" onClick={() => setAdding(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap ${
            adding
              ? 'text-gray-600 border border-gray-200 bg-white hover:bg-gray-50'
              : 'bg-teal-500 hover:bg-teal-600 text-white shadow-sm hover:shadow-md'
          }`}>
          <i className={`${adding ? 'ri-close-line' : 'ri-add-line'} text-sm`} />
          {adding ? '取消' : '新增类型'}
        </button>
      </div>

      {/* 新增表单 */}
      {adding && (
        <div className="mb-4 p-4 bg-white border border-teal-200 rounded-xl space-y-3 shadow-sm animate-[slideDown_0.2s_ease-out]">
          <div className="flex items-center gap-2 text-xs font-semibold text-teal-600">
            <i className="ri-add-circle-line text-base" />
            新增客户类型
          </div>
          <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="输入类型名称（必填）"
            className={inputCls} autoFocus />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setAddName(''); }}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white rounded-lg cursor-pointer hover:bg-gray-50">取消</button>
            <button type="button" onClick={handleAdd}
              className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer">确认添加</button>
          </div>
        </div>
      )}

      {/* 列表 */}
      {loading ? (
        <SkeletonList />
      ) : types.length === 0 ? (
        <EmptyState icon="ri-user-settings-line" text="暂无客户类型" hint="点击右上角新增类型" />
      ) : (
        <div className="space-y-2">
          {types.map((t) => (
            <div key={t.id}>
              {editId === t.id ? (
                <div className="p-4 bg-white border border-teal-200 rounded-xl space-y-3 shadow-sm animate-[slideDown_0.2s_ease-out]">
                  <div className="flex items-center gap-2 text-xs font-semibold text-teal-600">
                    <i className="ri-edit-line text-base" />
                    编辑类型
                  </div>
                  <input value={editName} onChange={e => setEditName(e.target.value)} className={inputCls} autoFocus />
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setEditId(null)}
                      className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white rounded-lg cursor-pointer hover:bg-gray-50">取消</button>
                    <button type="button" onClick={() => handleEdit(t.id)}
                      className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer">保存</button>
                  </div>
                </div>
              ) : (
                <div className="group flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl hover:border-teal-200 hover:shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg group-hover:bg-teal-100 transition-colors">
                      <i className={`${getTypeIcon(t.name)} text-teal-500 text-lg`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{t.name}</p>
                      {t.createdBy === null ? (
                        <span className="inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 bg-gray-100 text-gray-400 text-xs rounded">
                          <i className="ri-lock-line text-xs" />
                          系统预设
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 mt-0.5">自定义</span>
                      )}
                    </div>
                  </div>
                  {t.createdBy !== null && (
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => { setEditId(t.id); setEditName(t.name); }}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-500 hover:bg-teal-50 rounded-lg transition-all duration-150 cursor-pointer">
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      <button type="button" onClick={() => handleDelete(t.id)}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-150 cursor-pointer">
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 确认弹窗 */}
      <ConfirmDialog
        open={showConfirm}
        title="删除确认"
        message="确定要删除该客户类型吗？此操作不可撤销。"
        onConfirm={confirmDelete}
        onCancel={() => { setShowConfirm(false); setDeleteId(null); }}
        confirmText="确认删除"
        cancelText="取消"
        variant="danger"
      />

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

/* ========== 我的客户标签管理（标签库用户私有：系统预设 + 本人创建） ========== */
function CustomerTagsManager() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState(TAG_COLORS[0]);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState(TAG_COLORS[0]);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  /** 失败提示必须把服务端那句中文带回来：归属/预设锁定这类拒绝只有后端知道自己为什么挡 */
  const notify = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), type === 'error' ? 3500 : 2500);
  };
  const fail = (action: string, fallback: string) => (error: unknown) => {
    console.error(`${action}:`, error);
    notify(error instanceof Error && error.message ? error.message : fallback, 'error');
  };

  const { data: tags = [], isLoading: loading } = useQuery({
    queryKey: ['customerTags'],
    queryFn: async () => {
      const data = await get<CustomerTag[]>('/customer-tags');
      return data ?? [];
    },
  });

  // created_by 由服务端按登录态注入，客户端不再上送
  const addCustomerTagMutation = useMutation({
    mutationFn: async (customerTagData: { name: string; color: string }) => {
      return post('/customer-tags', customerTagData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerTags'] });
      setAddName('');
      setAddColor(TAG_COLORS[0]);
      setAdding(false);
      notify('客户标签添加成功');
    },
    onError: fail('添加客户标签失败', '添加失败，请重试'),
  });

  const editCustomerTagMutation = useMutation({
    mutationFn: async ({ id, name, color }: { id: string; name: string; color: string }) => {
      return put(`/customer-tags/${id}`, { name, color });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerTags'] });
      setEditId(null);
      notify('标签已更新');
    },
    onError: fail('编辑客户标签失败', '更新失败，请重试'),
  });

  const deleteCustomerTagMutation = useMutation({
    mutationFn: async (id: string) => {
      return del(`/customer-tags/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customerTags'] });
      setDeleteId(null);
      setShowConfirm(false);
      notify('标签已删除');
    },
    onError: fail('删除客户标签失败', '删除失败，请重试'),
  });

  const handleAdd = () => {
    if (!addName.trim()) return;
    addCustomerTagMutation.mutate({ name: addName.trim(), color: addColor });
  };

  const handleEdit = (id: string) => {
    if (!editName.trim()) return;
    editCustomerTagMutation.mutate({ id, name: editName.trim(), color: editColor });
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      deleteCustomerTagMutation.mutate(deleteId);
    }
  };

  const ColorPicker = ({ selected, onSelect }: { selected: string; onSelect: (c: string) => void }) => (
    <div className="flex flex-wrap gap-2">
      {TAG_COLORS.map(c => (
        <button key={c} type="button" onClick={() => onSelect(c)}
          style={{ backgroundColor: c }}
          className={`w-7 h-7 rounded-full cursor-pointer transition-all duration-150 ${
            selected === c
              ? 'ring-2 ring-offset-2 ring-teal-500 scale-110 shadow-md'
              : 'ring-1 ring-black/5 hover:scale-110 hover:shadow-sm'
          }`} />
      ))}
    </div>
  );

  return (
    <div>
      {/* 头部卡片 */}
      <div className="flex items-center justify-between mb-4 p-4 bg-white border border-gray-100 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl">
            <i className="ri-price-tag-3-line text-teal-500 text-lg" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-800">我的客户标签</p>
            <p className="text-xs text-gray-400 mt-0.5">仅本人可见的个性化标签库（含系统预设），为自己绑定的客户打标，不与他人共用</p>
          </div>
        </div>
        <button type="button" onClick={() => setAdding(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap ${
            adding
              ? 'text-gray-600 border border-gray-200 bg-white hover:bg-gray-50'
              : 'bg-teal-500 hover:bg-teal-600 text-white shadow-sm hover:shadow-md'
          }`}>
          <i className={`${adding ? 'ri-close-line' : 'ri-add-line'} text-sm`} />
          {adding ? '取消' : '新增标签'}
        </button>
      </div>

      {/* 新增表单 */}
      {adding && (
        <div className="mb-4 p-4 bg-white border border-teal-200 rounded-xl space-y-3 shadow-sm animate-[slideDown_0.2s_ease-out]">
          <div className="flex items-center gap-2 text-xs font-semibold text-teal-600">
            <i className="ri-add-circle-line text-base" />
            新增客户标签
          </div>
          <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="输入标签名称（必填）"
            className={inputCls} autoFocus />
          <div>
            <p className="text-xs text-gray-500 mb-2">选择颜色</p>
            <ColorPicker selected={addColor} onSelect={setAddColor} />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setAddName(''); setAddColor(TAG_COLORS[0]); }}
              className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white rounded-lg cursor-pointer hover:bg-gray-50">取消</button>
            <button type="button" onClick={handleAdd}
              className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer">确认添加</button>
          </div>
        </div>
      )}

      {/* 列表 */}
      {loading ? (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-100 rounded-full">
              <div className="w-3 h-3 rounded-full bg-gray-100 animate-pulse" />
              <div className="h-3 w-12 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      ) : tags.length === 0 ? (
        <EmptyState icon="ri-price-tag-3-line" text="暂无我的客户标签" hint="点击右上角创建自己的个性化标签" />
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map(t => (
            editId === t.id ? (
              <div key={t.id} className="w-full p-4 bg-white border border-teal-200 rounded-xl space-y-3 shadow-sm animate-[slideDown_0.2s_ease-out]">
                <div className="flex items-center gap-2 text-xs font-semibold text-teal-600">
                  <i className="ri-edit-line text-base" />
                  编辑标签
                </div>
                <input value={editName} onChange={e => setEditName(e.target.value)} className={inputCls} autoFocus />
                <div>
                  <p className="text-xs text-gray-500 mb-2">选择颜色</p>
                  <ColorPicker selected={editColor} onSelect={setEditColor} />
                </div>
                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setEditId(null)}
                    className="px-3 py-1.5 text-xs text-gray-600 border border-gray-200 bg-white rounded-lg cursor-pointer hover:bg-gray-50">取消</button>
                  <button type="button" onClick={() => handleEdit(t.id)}
                    className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer">保存</button>
                </div>
              </div>
            ) : (
              <div key={t.id}
                className={`group flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full border bg-white transition-all duration-200 ${
                  t.createdBy === null
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}>
                <span className="w-3 h-3 rounded-full shrink-0 ring-1 ring-black/5" style={{ backgroundColor: t.color ?? '#14b8a6' }} />
                <span className="text-sm text-gray-700">{t.name}</span>
                {t.createdBy === null ? (
                  <span className="flex items-center justify-center w-4 h-4">
                    <i className="ri-lock-line text-gray-300 text-xs" />
                  </span>
                ) : (
                  <div className="flex items-center gap-0.5 ml-0.5">
                    <button type="button" onClick={() => { setEditId(t.id); setEditName(t.name); setEditColor(t.color ?? TAG_COLORS[0]); }}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-teal-500 rounded cursor-pointer">
                      <i className="ri-pencil-line text-xs" />
                    </button>
                    <button type="button" onClick={() => handleDelete(t.id)}
                      className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 rounded cursor-pointer">
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                )}
              </div>
            )
          ))}
        </div>
      )}

      {/* 确认弹窗 */}
      <ConfirmDialog
        open={showConfirm}
        title="删除确认"
        message={tags.length <= 1
          ? '这是最后一个客户标签，系统要求至少保留一个标签，无法删除。如需替换，请先创建新标签再删除此标签。'
          : '确定要删除该标签吗？删除后已打标的展示将回退为原始值。提示：系统至少保留一个标签，最后一个无法删除。'}
        onConfirm={confirmDelete}
        onCancel={() => { setShowConfirm(false); setDeleteId(null); }}
        confirmText="确认删除"
        cancelText="取消"
        variant="danger"
      />

      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
}

/* ========== 导出主面板 ========== */
export default function CustomerSettingsPanel() {
  return (
    <div className="space-y-3 md:space-y-8">
      <CollapsibleSection title="客户类型">
        <CustomerTypesManager />
      </CollapsibleSection>
      <CollapsibleSection title="我的客户标签" defaultOpen={false}>
        <CustomerTagsManager />
      </CollapsibleSection>
    </div>
  );
}
